import { SESSION_STORAGE } from '../constants';

/**
 * Logout diagnostics (temporary, QA on stage).
 *
 * QA reports that after a while the app signs the user out of every tab. One tab reaching the
 * login screen is enough for that: the login page signs out, which removes the token every tab
 * shares in localStorage, and the other tabs follow. Nothing records *why* the first tab went to
 * the login screen, so every path that can send a tab there writes an entry here.
 *
 * The log lives in localStorage so it survives the reload that follows a logout and is shared by
 * all tabs. Read it from the console with `phdLogoutDiagnostics()` or in DevTools under
 * Application > Local Storage > `PhDsoft:LogoutDiagnostics`. It never holds the token itself.
 */
export const LOGOUT_DIAGNOSTICS_KEY = 'PhDsoft:LogoutDiagnostics';
export const LOGOUT_DIAGNOSTICS_MAX_ENTRIES = 50;

const MAX_STACK_LENGTH = 2000;

export interface LogoutDiagnosticEntry {
  at: string;
  reason: string;
  url: string;
  /** Random id of this page load: entries with the same id come from the same tab and load. */
  pageId: string;
  pageLoadedAt: string;
  /** The URL this page load started on. */
  bootUrl: string;
  visibility: string;
  online: boolean;
  token: { present: boolean; exp?: string; expired?: boolean };
  detail?: any;
  stack?: string;
}

const PAGE_ID = Math.random().toString(36).slice(2, 8);
const PAGE_LOADED_AT = new Date().toISOString();
const BOOT_URL = typeof window !== 'undefined' ? window.location.href : '';

/**
 * Appends an entry for `reason` to the shared log and echoes it to the console.
 * Never throws: a diagnostic must not change what the logout does.
 */
export function recordLogoutReason(reason: string, detail?: any): LogoutDiagnosticEntry | null {
  try {
    installLogoutDiagnosticsConsole();

    const entry: LogoutDiagnosticEntry = {
      at: new Date().toISOString(),
      reason,
      url: window.location.href,
      pageId: PAGE_ID,
      pageLoadedAt: PAGE_LOADED_AT,
      bootUrl: BOOT_URL,
      visibility: document.visibilityState,
      online: navigator.onLine,
      token: describeToken(localStorage.getItem(SESSION_STORAGE.TOKEN)),
      detail,
      stack: truncate(new Error('logout diagnostics').stack),
    };

    console.error('[LOGOUT-DIAG]', reason, entry);

    const entries = readLogoutDiagnostics();
    entries.push(entry);
    localStorage.setItem(LOGOUT_DIAGNOSTICS_KEY, JSON.stringify(entries.slice(-LOGOUT_DIAGNOSTICS_MAX_ENTRIES)));
    return entry;
  } catch {
    return null;
  }
}

export function readLogoutDiagnostics(): LogoutDiagnosticEntry[] {
  try {
    const parsed = JSON.parse(localStorage.getItem(LOGOUT_DIAGNOSTICS_KEY) ?? '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function clearLogoutDiagnostics(): void {
  try {
    localStorage.removeItem(LOGOUT_DIAGNOSTICS_KEY);
  } catch {
    // Nothing to clear.
  }
}

/** A plain, serialisable description of an error, an HTTP failure or any thrown value. */
export function describeError(error: any): any {
  if (error === null || error === undefined || typeof error !== 'object') {
    return { message: String(error) };
  }

  const described: any = {};
  if (error.name) { described.name = error.name; }
  if (error.message) { described.message = error.message; }
  if (error.status !== undefined) { described.status = error.status; }
  if (error.statusText) { described.statusText = error.statusText; }
  if (error.url) { described.url = stripQuery(error.url); }
  if (error.stack) { described.stack = truncate(error.stack); }
  return described;
}

/** Drops the query string, which may carry SAS signatures or other secrets. */
export function stripQuery(url: string): string {
  return typeof url === 'string' ? url.split('?')[0] : url;
}

/** Installs `window.phdLogoutDiagnostics()`, which prints the log and returns it as JSON. */
export function installLogoutDiagnosticsConsole(): void {
  const target = window as any;
  if (typeof target.phdLogoutDiagnostics === 'function') {
    return;
  }

  const show = () => {
    const entries = readLogoutDiagnostics();
    console.table(entries.map(e => ({ at: e.at, reason: e.reason, url: e.url, pageId: e.pageId, visibility: e.visibility, online: e.online, tokenExpired: e.token?.expired })));
    return JSON.stringify(entries, null, 2);
  };
  show.clear = clearLogoutDiagnostics;
  target.phdLogoutDiagnostics = show;
}

function describeToken(token: string | null): LogoutDiagnosticEntry['token'] {
  if (!token || token === 'null' || token === 'undefined') {
    return { present: false };
  }

  try {
    const payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const exp = JSON.parse(atob(payload.padEnd(Math.ceil(payload.length / 4) * 4, '='))).exp;
    if (typeof exp !== 'number') {
      return { present: true };
    }
    return { present: true, exp: new Date(exp * 1000).toISOString(), expired: Date.now() >= exp * 1000 };
  } catch {
    return { present: true };
  }
}

function truncate(text: string | undefined): string | undefined {
  return text && text.length > MAX_STACK_LENGTH ? text.substring(0, MAX_STACK_LENGTH) : text;
}
